import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { STATUS_META, STATUS_KEYS } from './rdTaskStatus';

/**
 * iter133 — R&D Dependencies / Tasks table.
 * Each task has an assignee, a status (on_track / advanced / delayed / blocked / done),
 * optional due date, free-text reason, and a list of other tasks that are blocking it.
 */
const EMPTY = { title: '', description: '', assignee_name: '', assignee_user_id: null, status: 'on_track', due_date: '', blocked_by_task_ids: [], blocking_reason: '' };

export default function RDTasks({ projectId, tasks, onChange }) {
  const { api } = useAuth();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState('all');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api().get('/rd/assignable-users').then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [api]);

  const taskById = useMemo(() => Object.fromEntries((tasks || []).map(t => [t.id, t])), [tasks]);
  const filtered = useMemo(() => {
    if (filter === 'all') return tasks || [];
    return (tasks || []).filter(t => t.status === filter);
  }, [tasks, filter]);

  const counts = useMemo(() => {
    const c = { all: tasks?.length || 0 };
    STATUS_KEYS.forEach(k => { c[k] = (tasks || []).filter(t => t.status === k).length; });
    return c;
  }, [tasks]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShow(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title || '',
      description: t.description || '',
      assignee_name: t.assignee_name || '',
      assignee_user_id: t.assignee_user_id || null,
      status: t.status || 'on_track',
      due_date: t.due_date || '',
      blocked_by_task_ids: t.blocked_by_task_ids || [],
      blocking_reason: t.blocking_reason || '',
    });
    setShow(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Task title required'); return; }
    const payload = {
      ...form,
      due_date: form.due_date || null,
      assignee_name: form.assignee_name || null,
      blocking_reason: form.blocking_reason || null,
    };
    try {
      if (editing) {
        await api().put(`/rd/projects/${projectId}/tasks/${editing.id}`, payload);
        toast.success('Task saved');
      } else {
        await api().post(`/rd/projects/${projectId}/tasks`, payload);
        toast.success('Task created');
      }
      setShow(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try { await api().delete(`/rd/projects/${projectId}/tasks/${t.id}`); toast.success('Deleted'); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const quickStatus = async (t, newStatus) => {
    try {
      await api().put(`/rd/projects/${projectId}/tasks/${t.id}`, { status: newStatus });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const toggleBlocker = (tid) => {
    setForm(f => {
      const has = f.blocked_by_task_ids.includes(tid);
      return { ...f, blocked_by_task_ids: has ? f.blocked_by_task_ids.filter(x => x !== tid) : [...f.blocked_by_task_ids, tid] };
    });
  };

  const availableBlockers = useMemo(() => (tasks || []).filter(t => !editing || t.id !== editing.id), [tasks, editing]);

  return (
    <Card className="p-5" data-testid="rd-tasks-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="font-semibold text-slate-800">Dependencies &amp; Workload</h2>
          <p className="text-xs text-slate-500">Who's doing what, what's blocking what, what's running ahead or behind.</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="rd-add-task-btn"><Plus className="w-4 h-4 mr-1" />New task</Button>
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {['all', ...STATUS_KEYS].map(k => {
          const label = k === 'all' ? 'All' : STATUS_META[k].label;
          const count = counts[k] || 0;
          const isSel = filter === k;
          const color = k === 'all' ? 'bg-slate-100 text-slate-700 border-slate-200' : STATUS_META[k].color;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`text-[11px] px-2 py-0.5 rounded border ${color} ${isSel ? 'ring-2 ring-fuchsia-300' : 'opacity-70 hover:opacity-100'}`}
              data-testid={`rd-task-filter-${k}`}
            >
              {label} · {count}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500">
          {filter === 'all' ? 'No tasks yet. Click "New task" to start parceling out the work.' : 'No tasks in this bucket.'}
        </p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-2 py-1.5 w-28">Status</th>
                <th className="text-left px-2 py-1.5">Task</th>
                <th className="text-left px-2 py-1.5 w-40">Assignee</th>
                <th className="text-left px-2 py-1.5 w-24">Due</th>
                <th className="text-left px-2 py-1.5">Blocked by</th>
                <th className="text-left px-2 py-1.5">Reason / notes</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const meta = STATUS_META[t.status] || STATUS_META.on_track;
                const StatusIcon = meta.Icon;
                return (
                  <tr key={t.id} className="border-t border-slate-200 hover:bg-slate-50/60" data-testid={`rd-task-row-${t.id}`}>
                    <td className="px-2 py-1.5">
                      <Select value={t.status} onValueChange={(v) => quickStatus(t, v)}>
                        <SelectTrigger className={`h-7 text-[11px] border ${meta.color}`} data-testid={`rd-task-status-${t.id}`}>
                          <span className="inline-flex items-center gap-1"><StatusIcon className="w-3 h-3" />{meta.label}</span>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_KEYS.map(k => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="font-medium text-slate-800">{t.title}</div>
                      {t.description && <div className="text-[11px] text-slate-500 line-clamp-2">{t.description}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-slate-700">{t.assignee_name || <span className="text-slate-400 italic">unassigned</span>}</td>
                    <td className="px-2 py-1.5 text-slate-600">{t.due_date || '—'}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {(t.blocked_by_task_ids || []).slice(0, 3).map(bid => {
                          const b = taskById[bid];
                          if (!b) return <Badge key={bid} variant="outline" className="text-[10px]">?</Badge>;
                          return <Badge key={bid} className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 border">{b.title.slice(0, 20)}</Badge>;
                        })}
                        {(t.blocked_by_task_ids || []).length > 3 && (
                          <Badge variant="outline" className="text-[10px]">+{t.blocked_by_task_ids.length - 3}</Badge>
                        )}
                        {(t.blocked_by_task_ids || []).length === 0 && <span className="text-slate-400 italic">—</span>}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">{t.blocking_reason || <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(t)} className="text-slate-500 hover:text-fuchsia-700 mr-2" data-testid={`rd-task-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(t)} className="text-rose-500 hover:text-rose-700" data-testid={`rd-task-remove-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-2xl" data-testid="rd-task-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Title</label>
              <Input data-testid="rd-task-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Assignee</label>
                <Select
                  value={form.assignee_user_id || '__none'}
                  onValueChange={v => {
                    const u = users.find(x => x.id === v);
                    setForm({ ...form, assignee_user_id: u ? u.id : null, assignee_name: u ? u.label : '' });
                  }}
                >
                  <SelectTrigger data-testid="rd-task-assignee-select"><SelectValue placeholder="Pick a teammate" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Not a system user / unassigned</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.label}{u.role ? ` · ${u.role}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!form.assignee_user_id && (
                  <Input className="mt-1.5" data-testid="rd-task-assignee" value={form.assignee_name} onChange={e => setForm({ ...form, assignee_name: e.target.value })} placeholder="…or type a name (contractor, vendor)" />
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Due date</label>
                <Input type="date" data-testid="rd-task-due" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Status</label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="rd-task-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_KEYS.map(k => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Reason / notes</label>
                <Input data-testid="rd-task-reason" value={form.blocking_reason} onChange={e => setForm({ ...form, blocking_reason: e.target.value })} placeholder="Why blocked / ahead of schedule?" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Description</label>
              <Textarea rows={2} data-testid="rd-task-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Blocked by</label>
              {availableBlockers.length === 0 ? (
                <p className="text-xs text-slate-400 italic mt-1">Add other tasks first, then pick them here.</p>
              ) : (
                <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-2 mt-1 space-y-1">
                  {availableBlockers.map(b => (
                    <label key={b.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                      <input
                        type="checkbox"
                        checked={form.blocked_by_task_ids.includes(b.id)}
                        onChange={() => toggleBlocker(b.id)}
                        data-testid={`rd-task-blocker-${b.id}`}
                      />
                      <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${STATUS_META[b.status]?.color || ''}`}>{STATUS_META[b.status]?.label || b.status}</span>
                      <span className="truncate">{b.title}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
            <Button onClick={save} data-testid="rd-task-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
